import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { LuChevronDown, LuChevronRight, LuPlus } from 'react-icons/lu';

import { Role } from '@ndvr/auth-api';
import { BillingGroup, GUID } from '@ndvr/billing-api';

import { FilterType } from '@/cip/_openapi/portfolio-optimizer';
import { BulkSelectionBar } from '@/components/BulkSelectionBar';
import { ConfirmModal } from '@/components/ConfirmModal';
import { ClientDataTable } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { EllipsisDropdownMenuTrigger } from '@/components/ui/dropdown-menu-triggers';
import { Page, PageContent, PageTitleRow, PageTopBar } from '@/components/ui/page-layout';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useUserHasRole } from 'views/admin/dev-queries';
import { useSelectedTenantGUIDs } from 'views/auth/tenant-selection';

import { useBillingGroupAccountNameMap, useBillingGroupActions } from './billing-group-hooks';
import { useBillingGroups } from './billing-group-queries';
import { BillingGroupAccountsTable } from './BillingGroupAccountsTable';
import { CreateBillingGroupDialog } from './CreateBillingGroupDialog';

export const BillingGroupsPage = () => {
    const { data: billingGroups = [], isLoading } = useBillingGroups();
    const { nameByGUID, namesLoading } = useBillingGroupAccountNameMap(billingGroups);
    const tenantGUIDs = useSelectedTenantGUIDs();
    const { data: canManage = false } = useUserHasRole(Role.CHIEF_COMPLIANCE_OFFICER);

    const [selectedGUIDs, setSelectedGUIDs] = useState<Set<GUID<BillingGroup>>>(new Set());
    const clearSelection = useCallback(() => setSelectedGUIDs(new Set()), []);
    const {
        activate,
        activateMutation,
        deactivateMutation,
        editingItem,
        dialogOpen,
        setDialogOpen,
        openCreate,
        openEdit,
        deactivatingGUIDs,
        requestDeactivate,
        cancelDeactivate,
        confirmDeactivate,
    } = useBillingGroupActions({ onActionSuccess: clearSelection });

    const canCreate = canManage && tenantGUIDs.length === 1;

    const toggleSelected = useCallback((billingGroupGUID: GUID<BillingGroup>, checked: boolean) => {
        setSelectedGUIDs((prev) => {
            const next = new Set(prev);
            if (checked) next.add(billingGroupGUID);
            else next.delete(billingGroupGUID);
            return next;
        });
    }, []);

    const selectedGroups = useMemo(
        () => billingGroups.filter((bg) => selectedGUIDs.has(bg.billingGroupGUID)),
        [billingGroups, selectedGUIDs],
    );
    const activeSelectedGUIDs = selectedGroups.filter((g) => g.active).map((g) => g.billingGroupGUID);
    const inactiveSelectedGUIDs = selectedGroups.filter((g) => !g.active).map((g) => g.billingGroupGUID);

    const columns = useMemo<ColumnDef<BillingGroup>[]>(
        () => [
            ...(canManage
                ? [
                      {
                          id: 'select',
                          enableResizing: false,
                          enableSorting: false,
                          meta: { headerClassName: 'w-[1%]', cellClassName: 'w-[1%]' },
                          header: ({ table }) => {
                              // Derive from the filtered+sorted row model so column filters
                              // don't cause us to select hidden billing groups.
                              const visibleIds = table.getRowModel().rows.map((r) => r.original.billingGroupGUID);
                              const allSelected =
                                  visibleIds.length > 0 && visibleIds.every((id) => selectedGUIDs.has(id));
                              const someSelected = visibleIds.some((id) => selectedGUIDs.has(id));
                              return (
                                  <Checkbox
                                      checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                                      onCheckedChange={(checked) =>
                                          setSelectedGUIDs((prev) => {
                                              const next = new Set(prev);
                                              visibleIds.forEach((id) =>
                                                  checked === true ? next.add(id) : next.delete(id),
                                              );
                                              return next;
                                          })
                                      }
                                      aria-label="Select all billing groups"
                                  />
                              );
                          },
                          cell: ({ row }) => (
                              <Checkbox
                                  checked={selectedGUIDs.has(row.original.billingGroupGUID)}
                                  onCheckedChange={(checked) =>
                                      toggleSelected(row.original.billingGroupGUID, checked === true)
                                  }
                                  aria-label="Select billing group"
                                  onClick={(e) => e.stopPropagation()}
                              />
                          ),
                      } as ColumnDef<BillingGroup>,
                  ]
                : []),
            {
                id: 'expander',
                enableResizing: false,
                enableSorting: false,
                meta: { headerClassName: 'w-[1%] whitespace-nowrap', cellClassName: 'w-[1%] whitespace-nowrap' },
                cell: ({ row }) =>
                    row.getCanExpand() ? (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            aria-label={row.getIsExpanded() ? 'Collapse' : 'Expand'}
                            onClick={row.getToggleExpandedHandler()}
                        >
                            {row.getIsExpanded() ? (
                                <LuChevronDown className="h-4 w-4" />
                            ) : (
                                <LuChevronRight className="h-4 w-4" />
                            )}
                        </Button>
                    ) : null,
            },
            {
                accessorKey: 'billingGroupName',
                header: 'Billing Group',
                sortingFn: (a, b, id) =>
                    String(a.getValue(id) ?? '').localeCompare(String(b.getValue(id) ?? ''), undefined, {
                        sensitivity: 'base',
                    }),
                cell: ({ row }) => {
                    const count = row.original.accounts.length;
                    return (
                        <div className="flex flex-col">
                            <span className="font-medium">{row.original.billingGroupName}</span>
                            <span className="text-muted-foreground text-xs">
                                {count} {count === 1 ? 'account' : 'accounts'}
                            </span>
                        </div>
                    );
                },
                meta: { filterType: FilterType.STRING },
            },
            {
                id: 'feeScheduleName',
                accessorFn: (row) => row.feeSchedule.feeScheduleName,
                header: 'Fee Schedule',
                cell: ({ getValue }) => getValue<string>() ?? '—',
                meta: { filterType: FilterType.STRING },
            },
            {
                accessorKey: 'active',
                header: 'Status',
                cell: ({ getValue }) => (getValue<boolean>() ? 'Active' : 'Inactive'),
                meta: { filterType: FilterType.BOOLEAN },
            },
            {
                id: 'actions',
                enableResizing: false,
                enableSorting: false,
                cell: ({ row }) =>
                    canManage ? (
                        <div className="flex justify-end">
                            <DropdownMenu>
                                <EllipsisDropdownMenuTrigger />
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => openEdit(row.original)}>Edit</DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    {row.original.active ? (
                                        <DropdownMenuItem
                                            className="text-destructive focus:text-destructive"
                                            onClick={() => requestDeactivate([row.original.billingGroupGUID])}
                                        >
                                            Deactivate
                                        </DropdownMenuItem>
                                    ) : (
                                        <DropdownMenuItem
                                            onClick={() => activateMutation.mutate([row.original.billingGroupGUID])}
                                        >
                                            Activate
                                        </DropdownMenuItem>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    ) : null,
            },
        ],
        [canManage, activateMutation, selectedGUIDs, toggleSelected, openEdit, requestDeactivate],
    );

    return (
        <Page>
            <PageTopBar />
            <PageTitleRow
                title="Billing Groups"
                actions={
                    canManage && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span>
                                    <Button
                                        variant="outline2"
                                        size="sm"
                                        className="px-3"
                                        disabled={tenantGUIDs.length !== 1}
                                        onClick={openCreate}
                                    >
                                        <LuPlus className="h-4 w-4" /> New Billing Group
                                    </Button>
                                </span>
                            </TooltipTrigger>
                            {tenantGUIDs.length !== 1 && (
                                <TooltipContent>Select a single tenant to create a billing group</TooltipContent>
                            )}
                        </Tooltip>
                    )
                }
            />

            <PageContent>
                <ClientDataTable
                    className={selectedGroups.length > 0 ? 'pb-18' : undefined}
                    data={billingGroups}
                    columns={columns}
                    isLoading={isLoading}
                    emptyMessage="No billing groups found."
                    initialState={{ sorting: [{ id: 'billingGroupName', desc: false }] }}
                    getRowCanExpand={(row) => row.original.accounts.length > 0}
                    renderRowDetails={(group) => (
                        <BillingGroupAccountsTable
                            accounts={group.accounts}
                            nameByGUID={nameByGUID}
                            namesLoading={namesLoading}
                            canManage={canManage}
                        />
                    )}
                />
            </PageContent>

            {canManage && selectedGroups.length > 0 && (
                <BulkSelectionBar
                    selectionLabel={`${selectedGroups.length} ${selectedGroups.length === 1 ? 'billing group' : 'billing groups'}`}
                    onClear={clearSelection}
                >
                    {inactiveSelectedGUIDs.length > 0 && (
                        <Button
                            size="sm"
                            disabled={activateMutation.isPending}
                            onClick={() => activate(inactiveSelectedGUIDs)}
                        >
                            Activate
                        </Button>
                    )}
                    {activeSelectedGUIDs.length > 0 && (
                        <Button
                            variant="destructive"
                            size="sm"
                            disabled={deactivateMutation.isPending}
                            onClick={() => requestDeactivate(activeSelectedGUIDs)}
                        >
                            Deactivate
                        </Button>
                    )}
                </BulkSelectionBar>
            )}

            {canManage && (editingItem || canCreate) && (
                <CreateBillingGroupDialog
                    tenantGUID={editingItem ? editingItem.tenantGUID : tenantGUIDs[0]}
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                    billingGroup={editingItem ?? undefined}
                />
            )}

            <ConfirmModal
                open={!!deactivatingGUIDs}
                onOpenChange={(open) => !open && cancelDeactivate()}
                title={
                    deactivatingGUIDs && deactivatingGUIDs.length > 1
                        ? 'Deactivate Billing Groups'
                        : 'Deactivate Billing Group'
                }
                description={
                    deactivatingGUIDs && deactivatingGUIDs.length > 1
                        ? `Are you sure you want to deactivate these ${deactivatingGUIDs.length} billing groups?`
                        : 'Are you sure you want to deactivate this billing group?'
                }
                confirmText="Deactivate"
                confirmVariant="destructive"
                onConfirm={confirmDeactivate}
                loading={deactivateMutation.isPending}
                closeOnConfirm={false}
            />
        </Page>
    );
};
